/* eslint-disable @typescript-eslint/no-explicit-any */
// @todo fix `any`

import qs from 'qs';
import xxhash from 'xxhash-wasm';

import {
  AbortError,
  ClientOutdatedError,
  DataError,
  DbError,
  ExpiredSessionError,
  NoSessionError,
  NotFoundError,
  RequestError,
  UniqueViolationError,
  ValidationError,
} from '@/renderer/utils/errors';
import { AuthRefreshResult, forceLogoutEventTarget, refreshToken } from '@/renderer/utils/useMe';
import type { JSONValue } from '@/types/json';
import type { RequestCache, RequestOpts, RequestSchema } from '@/types/request';

import { dispatchOutdatedAlert, dispatchShowAlert } from './alerts';
import { IS_SERVER } from './ssr';

interface TWithCacheKey<DataType> {
  data: DataType;
  authState: Awaited<ReturnType<typeof refreshToken>>;
  cacheKey?: string;
}
interface TWithResponse<DataType> extends TWithCacheKey<DataType> {
  response: Response;
}

const APP_DATE = parseInt(`${import.meta.env.VITE_PUBLIC_ENV__APP_DATE}`, 10) || 0;

const BASE_HEADERS = {
  'Content-Type': 'application/json',
  'x-ras-cd': `${APP_DATE}`,
};

/** Ids is in case the DB were replaced with a UUID system, then you only have to change these methods */
export const Ids = (a?: API.Id | string) => {
  const real = a === undefined ? NaN : parseInt(`${a}`, 10);
  const nan = Number.isNaN(real);
  const min = nan ? -Infinity : real;
  // const max = nan ? Infinity : real;

  return {
    gt: (b?: API.Id | string) => min > (b === undefined ? NaN : parseInt(`${b}`, 10)),
    gte: (b?: API.Id | string) => min >= (b === undefined ? NaN : parseInt(`${b}`, 10)),
    lt: (b?: API.Id | string) => min < (b === undefined ? NaN : parseInt(`${b}`, 10)),
    lte: (b?: API.Id | string) => min <= (b === undefined ? NaN : parseInt(`${b}`, 10)),
  };
};
const NO_REFRESH_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/refresh',
  '/api/v1/auth/register',
  '/api/v1/auth/logout',
];

// This is a temporary solution to prevent having to hit the API on every page load of the index, which has a
// +data.shared fetcher. This cache doesn't exist in Cloudflare Workers, as this context doesn't exist.
// It also only works on the second page load of the home page. In summary:
// @todo `request` needs to be migrated to a data store which is hydrated into React client-side.
export async function createRequestKey(
  request: RequestInfo | URL,
  init?: RequestInit,
  cacheOpts?: RequestCache,
) {
  let method = 'get';
  let url = !request ? '' : request;
  let headers = '';

  const requestIsRequest = request instanceof Request;

  if (requestIsRequest) {
    url = request?.url || url;
    method = init?.method || request?.method || method;
  }

  const withCredentials = cacheOpts?.withCredentials;
  const withHeaders = cacheOpts?.withHeaders;

  if (withHeaders || withCredentials) {
    let headerEntries: [string, string][] | undefined = undefined;

    if (typeof init?.headers?.entries === 'function') {
      // init.headers is a Headers obj
      if (requestIsRequest) {
        // merge these two
        headerEntries = [
          ...(request.headers?.entries
            ? // both init.headers and request.headers are Headers
              Object.entries({
                ...Object.fromEntries(request.headers.entries()),
                ...Object.fromEntries(init.headers.entries() as IterableIterator<[string, string]>),
              })
            : (init.headers.entries() as IterableIterator<[string, string]>)),
        ];
      } else {
        // only have init.headers as Headers
        headerEntries = [...(init.headers.entries() as IterableIterator<[string, string]>)];
      }
    } else if (init?.headers || requestIsRequest) {
      // init.headers is a normal obj (or not given), and request is a Request (or not)
      headerEntries = [
        ...Object.entries({
          ...(requestIsRequest && request.headers?.entries
            ? Object.fromEntries(request.headers.entries())
            : undefined),
          ...(init?.headers ? init.headers : undefined),
        }),
      ];
    }

    if (headerEntries?.length) {
      const promises = [];

      for (const [key, value] of headerEntries) {
        const lowerKey = key.toLowerCase();

        if (lowerKey === 'cookie' && withCredentials) {
          // Do not allow cookies to escape in plaintext
          const SESSION_NAME = import.meta.env.VITE_PUBLIC_ENV__SESSION_NAME || 'sid';

          promises.push(
            (async () => {
              const cookies = value.split(';').map((cookie) => cookie.trim());
              const filteredCookies = cookies.filter((cookie) => {
                const [name] = cookie.split('=');
                return name === SESSION_NAME;
              });

              if (filteredCookies.length) {
                const { h64ToString } = await xxhash();

                return `${lowerKey}=${await h64ToString(filteredCookies.join('; '))}`;
              }

              return '';
            })(),
          );
        } else if (lowerKey === 'authorization' && withCredentials) {
          // Do not allow authorizations to escape in plaintext
          promises.push(
            (async () => {
              const { h64ToString } = await xxhash();

              return `${lowerKey}=${await h64ToString(value)}`;
            })(),
          );
        } else if (withHeaders?.includes(lowerKey)) {
          promises.push(`${lowerKey}=${value}`);
        }
      }

      headers = (await Promise.all(promises)).join('&');
    }
  }

  return `${method}:${httpUnPad(`${url}`)}${headers ? ':' : ''}${headers}`;
}

const hasRealCache = typeof caches !== 'undefined';
const mapCache = new Map();
let realCache: Cache | null = null;

async function getCache() {
  if (hasRealCache) {
    if (!realCache) {
      realCache = await caches.open('ras:request');
    }

    return realCache;
  }
}

const cache =
  // Create a fake cacher for when we are not in a Web Cache API context (like Service Workers or Cloudflare Worker)
  {
    async delete(
      request: RequestInfo | URL,
      init?: RequestInit,
      options?: CacheQueryOptions,
      cacheKey?: string,
    ): Promise<boolean> {
      const realCache = await getCache();

      return realCache
        ? await realCache.delete(request, options)
        : mapCache.delete(cacheKey || (await createRequestKey(request, init)));
    },
    async match(
      request: RequestInfo | URL,
      init?: RequestInit,
      options?: CacheQueryOptions,
      cacheKey?: string,
    ): Promise<Response | undefined> {
      const realCache = await getCache();

      return realCache
        ? await realCache.match(request, options)
        : await mapCache.get(cacheKey || (await createRequestKey(request, init)))?.clone?.();
    },
    async put(
      request: RequestInfo | URL,
      init: RequestInit | undefined,
      response: Response,
      cacheKey?: string,
    ): Promise<void> {
      const realCache = await getCache();

      realCache
        ? await realCache.put(request, response)
        : mapCache.set(cacheKey || (await createRequestKey(request, init)), response);
    },
  };

function httpPad(str: string) {
  const urlBase =
    (IS_SERVER ? import.meta.env.VITE_PRIVATE_ENV__API_URL : '') ||
    import.meta.env.VITE_PUBLIC_ENV__API_URL ||
    (typeof location !== 'undefined'
      ? location.origin
      : import.meta.env.DEV
        ? 'http://localhost:3000'
        : '');

  return !/^https?:\/\//i.test(str) ? `${urlBase}/${str[0] === '/' ? str.slice(1) : str}` : str;
}

function httpUnPad(str: string) {
  const urlBase =
    (IS_SERVER ? import.meta.env.VITE_PRIVATE_ENV__API_URL : '') ||
    import.meta.env.VITE_PUBLIC_ENV__API_URL ||
    (typeof location !== 'undefined'
      ? location.origin
      : import.meta.env.DEV
        ? 'http://localhost:3000'
        : '');
  const urlBaseClient =
    import.meta.env.VITE_PUBLIC_ENV__API_URL ||
    (typeof location !== 'undefined'
      ? location.origin
      : import.meta.env.DEV
        ? 'http://localhost:3000'
        : '');

  if (str?.startsWith?.(urlBase)) {
    return str.slice(urlBase.length);
  }

  if (str?.startsWith?.(urlBaseClient)) {
    return str.slice(urlBaseClient.length);
  }

  return str;
}

export async function requestCacheKey<
  const TOut,
  const TBody = undefined | void | any,
  const TQuery = JSONValue,
  const I extends RequestInfo = RequestInfo,
  const WR extends boolean = false,
  const NS extends boolean = false,
  const WC extends boolean = false,
>(
  req: RequestSchema<TOut, TBody, I, TQuery> | string,
  opts?: RequestOpts<TBody, TQuery, WR, NS, WC>,
): Promise<string> {
  const isString = typeof req === 'string';
  let urlreq: string | I | Request = isString
    ? httpPad(req)
    : typeof req.input === 'string'
      ? httpPad(req.input)
      : req.input;

  const query = !!opts?.query && qs.stringify(opts.query, { arrayFormat: 'brackets' });
  const init = isString ? undefined : req.init;

  if (!urlreq) {
    throw new Error('request: Invalid request schema or url');
  }

  if (query) {
    if (typeof urlreq === 'string') {
      urlreq += (urlreq.includes('?') ? '&' : '?') + query;
    } else {
      urlreq = new Request(urlreq.url + (urlreq.url.includes('?') ? '&' : '?') + query, urlreq);
    }
  }

  return await createRequestKey(urlreq, init, opts?.cache);
}

export async function request<
  const TOut,
  const TBody = undefined | void | any,
  const TQuery = JSONValue,
  const I extends RequestInfo = RequestInfo,
  const WR extends boolean = false,
  const NS extends boolean = false,
  const WC extends boolean = false,
>(
  req: RequestSchema<TOut, TBody, I, TQuery> | string,
  opts?: RequestOpts<TBody, TQuery, WR, NS, WC>,
): Promise<WR extends true ? TWithResponse<TOut> : WC extends true ? TWithCacheKey<TOut> : TOut> {
  type FinalType = WR extends true
    ? TWithResponse<TOut>
    : WC extends true
      ? TWithCacheKey<TOut>
      : TOut;

  const isString = typeof req === 'string';
  let urlreq: string | I | Request = isString
    ? httpPad(req)
    : typeof req.input === 'string'
      ? httpPad(req.input)
      : req.input;

  const query = !!opts?.query && qs.stringify(opts.query, { arrayFormat: 'brackets' });
  const method = opts?.method || (isString ? undefined : req.method);
  const init = isString ? undefined : req.init;

  if (!urlreq) {
    throw new Error('request: Invalid request schema or url');
  }

  let url: URL;

  if (query) {
    if (typeof urlreq === 'string') {
      url = new URL(urlreq);
      urlreq += (urlreq.includes('?') ? '&' : '?') + query;
    } else {
      url = new URL(urlreq.url);
      urlreq = new Request(urlreq.url + (urlreq.url.includes('?') ? '&' : '?') + query, urlreq);
    }
  } else {
    if (typeof urlreq === 'string') {
      url = new URL(urlreq);
    } else {
      url = new URL(urlreq.url);
    }
  }

  let expiresAt: number = 0;
  const timestamp: number = Date.now();
  let cacheState: 'delete' | 'cached' | 'invalid' = 'invalid';
  let response: Response | undefined = undefined;
  let cacheKey: string | undefined = opts?.cacheKey;
  const id = (req as RequestSchema<TOut, TBody, I, TQuery>)?.id;
  const name = (req as RequestSchema<TOut, TBody, I, TQuery>)?.name;

  const cacheOpts = opts?.cache;

  try {
    if (
      cache &&
      (!cacheOpts?.on ||
        cacheOpts?.on === 'both' ||
        (IS_SERVER && cacheOpts?.on === 'server') ||
        (!IS_SERVER && cacheOpts?.on === 'client'))
    ) {
      if (cacheOpts?.expires) {
        cacheKey = cacheKey || (await createRequestKey(urlreq, init, cacheOpts));
        response = response || (await cache.match(urlreq, init, undefined, cacheKey));

        if (response) {
          const expiresAt = Number.parseInt(response.headers.get('x-ras-cache-expires') || '0', 10);

          if (expiresAt <= timestamp) {
            cacheState = 'delete';
            response = undefined;
          } else {
            cacheState = 'cached';
          }
        }

        expiresAt = +cacheOpts.expires;
      }

      if (cacheOpts?.maxAge) {
        cacheKey = cacheKey || (await createRequestKey(urlreq, init, cacheOpts));
        response = response || (await cache.match(urlreq, init, undefined, cacheKey));

        if (response) {
          const timestamped = Number.parseInt(
            response.headers.get('x-ras-cache-timestamp') || '0',
            10,
          );

          if (timestamp - timestamped >= cacheOpts.maxAge) {
            cacheState = 'delete';
            response = undefined;
          } else {
            cacheState = 'cached';
          }
        }

        expiresAt = timestamp + cacheOpts.maxAge;
      }
    }
  } catch (error) {
    console.error(error);
  }

  const shouldSkip = !!opts?.skipRefresh || NO_REFRESH_PATHS.includes(url.pathname);

  let refreshResult: Awaited<ReturnType<typeof refreshToken>> | null = null;

  if (!shouldSkip && !IS_SERVER) {
    // Preemptively check if we need to refresh the token based on localStorage status
    // On the server, we always skip this preemptive token refresher -- we refresh after error below
    refreshResult = await refreshToken({
      submessage: url.pathname,
      fetch: opts?.fetch,
      authToken: opts?.authToken,
    });

    const { status } = refreshResult;

    if (status === AuthRefreshResult.EXPIRED || status === AuthRefreshResult.ERROR) {
      // This only happens if the user is logged in AND the session cannot be refreshed
      throw new ExpiredSessionError();
    }
  }

  const body =
    opts?.body || ((opts as { data: any })?.data as RequestOpts<TBody, TQuery, WR, NS, WC>['body']);

  const fetcher = opts?.fetch || fetch;

  let data!: TOut;
  let errdata;

  if (!response) {
    try {
      response = await fetcher(urlreq, {
        // Cloudflare Workers don't support fetch.credentials
        credentials: IS_SERVER ? undefined : 'include',
        // Cloudflare Workers don't support fetch.mode
        mode: IS_SERVER ? undefined : 'cors',
        method: method,
        headers: BASE_HEADERS,
        body: body ? (opts?.noStringify ? (body as string) : JSON.stringify(body)) : undefined,
        // Workers doesn't support the `cache` keyword, but shouldn't be needed anyways.
        // This is more for clients to prevent stale from cache. Still requests, but if unchanged, uses cache.
        cache: IS_SERVER ? undefined : 'no-cache',
        ...init,
      });
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        throw new AbortError(error);
      } else {
        throw error;
      }
    }

    cacheState = 'invalid';
  }

  // Create a new response with the cloned body and original headers
  const clone = opts?.withResponse || expiresAt ? await response.clone() : null;

  try {
    data = (await response.json()) as TOut;
  } catch (_error) {
    // ignore json errs
    if (typeof response.statusText === 'string' && response.statusText[0] === '{') {
      try {
        errdata = JSON.parse(response.statusText);
      } catch (_err) {
        // ignore json errs
        errdata = response.statusText;
      }
    } else {
      errdata = response.statusText;
    }
  }

  if (!response.ok || errdata) {
    const dataOrErr = data || errdata || response.statusText;
    const sendObj =
      typeof dataOrErr === 'string'
        ? { message: dataOrErr, code: response.status }
        : { ...dataOrErr, code: response.status };

    const nextErr =
      dataOrErr?.name === 'NotFoundError' || response.status === 404
        ? new NotFoundError(sendObj)
        : dataOrErr.message === 'API.CLIENT.OUTDATED.ERROR'
          ? new ClientOutdatedError(sendObj)
          : dataOrErr?.name === 'ValidationError'
            ? new ValidationError(sendObj)
            : dataOrErr?.name === 'UniqueViolationError'
              ? new UniqueViolationError(sendObj)
              : response.status === 401
                ? dataOrErr?.expired === true
                  ? new ExpiredSessionError(sendObj)
                  : new NoSessionError(sendObj)
                : dataOrErr?.name === 'DBError'
                  ? new DbError(sendObj)
                  : dataOrErr?.name === 'DataError'
                    ? new DataError(sendObj)
                    : new RequestError(sendObj);

    if (nextErr instanceof ExpiredSessionError && !shouldSkip) {
      // In SSR requests, the session gets refreshed if expired at page load, so we might not have an authToken in localStorage
      refreshResult = await refreshToken({
        forceIfUnknown: true,
        // forceIfEarly: true,
        submessage: url.pathname,
        fetch: opts?.fetch,
        authToken: opts?.authToken,
      });

      const { status } = refreshResult;

      if (status === AuthRefreshResult.SUCCESS) {
        // Successfully refreshed the session, so re-run this request but don't let it attempt to refresh again
        return (await request(req, {
          ...opts,
          skipRefresh: true,
          // withResponse: false,
          // withCacheKey: false,
        })) as FinalType;
      }
    }

    if (nextErr instanceof ClientOutdatedError) {
      dispatchOutdatedAlert({
        message: nextErr.message,
        submessage: `${method || 'GET'} ${response.url.replace(
          /^https?:\/\/[a-z0-9_.:-]+\/api\/v1/,
          '',
        )}`,
      });
    } else if (!opts?.hideAlert) {
      // Generate an ID (or use one if given). We use this to avoid duplicate error messages spamming the screen
      const alertKey = id || name || cacheKey || (await createRequestKey(urlreq, init, cacheOpts));

      let str = response.url.replace(/^https?:\/\/[a-z0-9_.:-]+\/api\/v1/, '');
      let pre: string | undefined = undefined;

      const hasNoSession = nextErr instanceof NoSessionError;

      if (!IS_SERVER && hasNoSession) {
        forceLogoutEventTarget.dispatchEvent(new CustomEvent('ras:forceLogout'));
      }

      try {
        // Make this error URL prettier
        const parsed = new URL(response.url);

        str = parsed.pathname.replace('/api/v1/', '');

        const querystr = parsed.search?.slice?.(1);
        const query = querystr?.length && !hasNoSession ? qs.parse(querystr) : undefined;

        if (query) {
          pre = JSON.stringify(query);
        }
      } catch (_error) {
        // ignore
      }

      dispatchShowAlert({
        id: alertKey,
        status: response.status >= 401 && response.status <= 404 ? 'warning' : 'error',
        message: nextErr.message,
        submessage: `${method || 'GET'} ${str}`,
        pre,
      });
    }

    throw nextErr;
  }

  let result = data as FinalType;

  if (opts?.withResponse) {
    result = {
      response: clone as Response,
      data,
      authState: refreshResult,
      cacheKey,
    } as FinalType;
  } else if (opts?.withCacheKey) {
    if (opts?.withResponse) {
      result = {
        data,
        authState: refreshResult,
        cacheKey,
      } as FinalType;
    }
  }

  try {
    if (cache) {
      if (clone && expiresAt !== 0 && cacheState === 'invalid' && expiresAt > timestamp) {
        if (cacheState === 'invalid' && expiresAt > timestamp) {
          const blob = await (await clone.clone()).body;

          // We have to clone it this way, otherwise `headers` is immutable
          const cacheClone = new Response(blob, {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            // Ignore this error; it exists: https://developer.mozilla.org/en-US/docs/Web/API/Response/type
            type: response.type,
            url: response.url,
            redirected: response.redirected,
            ok: response.ok,
          });

          cacheClone.headers.set('x-ras-cache-timestamp', `${timestamp}`);
          cacheClone.headers.set('x-ras-cache-expires', `${expiresAt}`);
          if (!cacheClone.headers.get('Expires')) {
            cacheClone.headers.set('Expires', new Date(expiresAt).toUTCString()); // for Cloudflare Workers Cache API
          }

          cacheKey = cacheKey || (await createRequestKey(urlreq, init, cacheOpts));
          /*await*/ cache.put(urlreq, init, cacheClone, cacheKey);
        }
      }

      if (cacheState === 'delete') {
        cacheKey = cacheKey || (await createRequestKey(urlreq, init, cacheOpts));
        /*await*/ cache.delete(urlreq, init, undefined, cacheKey);
      }
    }
  } catch (error) {
    console.error(error);
  }

  if (opts?.withResponse && (cacheKey || opts?.withCacheKey)) {
    (result as TWithResponse<TOut>).cacheKey =
      cacheKey || (await createRequestKey(urlreq, init, cacheOpts));
  }

  return result as FinalType;
}
